import BrainKnowledgeAPI from 'dashboard/api/brain/knowledge';
import { throwErrorMessage } from 'dashboard/store/utils/api';
import { createStore } from '../storeFactory';

const SET_PENDING_COUNT = 'SET_KNOWLEDGE_PENDING_COUNT';
let latestListRequestId = 0;
let latestPendingCountRequestId = 0;

export default createStore({
  name: 'BrainKnowledge',
  API: BrainKnowledgeAPI,
  getters: {
    getPendingCount: state => state.meta.pendingCount || 0,
  },
  mutations: {
    [SET_PENDING_COUNT](state, count) {
      state.meta = { ...state.meta, pendingCount: Number(count) };
    },
  },
  actions: mutations => ({
    get: async ({ commit }, params = {}) => {
      latestListRequestId += 1;
      const requestId = latestListRequestId;
      commit(mutations.SET_UI_FLAG, { fetchingList: true });
      try {
        const response = await BrainKnowledgeAPI.get(params);
        if (requestId !== latestListRequestId) return response.data.payload;

        commit(mutations.SET, response.data.payload);
        commit(mutations.SET_META, response.data.meta);
        return response.data.payload;
      } catch (error) {
        if (requestId !== latestListRequestId) return [];
        return throwErrorMessage(error);
      } finally {
        if (requestId === latestListRequestId) {
          commit(mutations.SET_UI_FLAG, { fetchingList: false });
        }
      }
    },
    removeBulkKnowledge: ({ commit, state }, ids) => {
      const updated = state.records.filter(r => !ids.includes(r.id));
      commit(mutations.SET, updated);
    },
    clearRecords: ({ commit }) => {
      latestListRequestId += 1;
      latestPendingCountRequestId += 1;
      commit(mutations.SET, []);
      commit(mutations.SET_META, { total_count: 0, page: 1 });
      commit(SET_PENDING_COUNT, 0);
      commit(mutations.SET_UI_FLAG, { fetchingList: false });
    },
    // Override default delete: API.delete handles {id, assistantId} payload but
    // the shared storeFactory mutation expects a scalar id (record.id !== id).
    // Without this override the local store stays stale until the next refetch
    // and bulk-delete callers' "is this page now empty?" checks return false
    // even after the records were deleted server-side.
    delete: async ({ commit }, payload) => {
      const id = payload?.id ?? payload;
      commit(mutations.SET_UI_FLAG, { deletingItem: true });
      try {
        await BrainKnowledgeAPI.delete(payload);
        commit(mutations.DELETE, id);
        return id;
      } finally {
        commit(mutations.SET_UI_FLAG, { deletingItem: false });
      }
    },
    activate: async ({ commit }, { id, assistantId }) => {
      commit(mutations.SET_UI_FLAG, { updatingItem: true });
      try {
        const response = await BrainKnowledgeAPI.activate({
          id,
          assistantId,
        });
        commit(mutations.EDIT, response.data);
        return response.data;
      } catch (error) {
        // Surface the localized backend message (e.g. combined budget
        // exceeded) instead of a raw Axios "Request failed with status
        // code 422", as the shared store helpers already do.
        return throwErrorMessage(error);
      } finally {
        commit(mutations.SET_UI_FLAG, { updatingItem: false });
      }
    },
    archive: async ({ commit }, { id, assistantId }) => {
      commit(mutations.SET_UI_FLAG, { updatingItem: true });
      try {
        const response = await BrainKnowledgeAPI.archive({
          id,
          assistantId,
        });
        commit(mutations.EDIT, response.data);
        return response.data;
      } finally {
        commit(mutations.SET_UI_FLAG, { updatingItem: false });
      }
    },
    bulkActivate: async (_, { ids, assistantId }) => {
      try {
        const response = await BrainKnowledgeAPI.bulkActions({
          ids,
          action: 'activate',
          assistantId,
        });
        // Return { succeeded, failed } so callers can detect 207 partial-failure.
        return response.data;
      } catch (error) {
        // Whole-batch failure (e.g. combined budget exceeded) returns HTTP
        // 422 with body { succeeded: [], failed: [{ id, error }] } (see
        // render_bulk_result). The localized reason lives in failed[].error,
        // which throwErrorMessage/parseAPIErrorResponse does NOT read, so
        // pull it out explicitly before falling back.
        const failed = error?.response?.data?.failed;
        if (Array.isArray(failed) && failed.length > 0 && failed[0]?.error) {
          throw new Error(failed[0].error);
        }
        return throwErrorMessage(error);
      }
    },
    bulkDelete: async (_, { ids, assistantId }) => {
      const response = await BrainKnowledgeAPI.bulkActions({
        ids,
        action: 'archive',
        assistantId,
      });
      // Return { succeeded, failed } so callers can detect 207 partial-failure.
      return response.data;
    },
    fetchPendingCount: async ({ commit }, assistantId) => {
      latestPendingCountRequestId += 1;
      const requestId = latestPendingCountRequestId;
      try {
        const response = await BrainKnowledgeAPI.get({
          status: 'pending',
          page: 1,
          assistantId,
        });
        if (requestId !== latestPendingCountRequestId) return;

        const count = response.data?.meta?.total_count || 0;
        commit(SET_PENDING_COUNT, count);
      } catch (error) {
        if (requestId === latestPendingCountRequestId) {
          commit(SET_PENDING_COUNT, 0);
        }
      }
    },
  }),
});
